import ApplicationController from './application_controller';

/**
 * Nav Search Controller
 *
 * Swaps the resource-centre navbar's Area of Focus tabs for a compact search
 * bar once the page's own search block has scrolled up under the navbar.
 *
 * Usage:
 *   <div data-controller="nav-search">
 *     <div data-nav-search-target="tabs">...</div>
 *     <div data-nav-search-target="search" class="opacity-0 invisible">...</div>
 *   </div>
 *
 * The observed element lives in <main>, outside this controller's element, so
 * it is found by selector rather than declared as a Stimulus target.
 *
 * Values:
 *   sentinel: selector for the page's search block (default: the data attribute
 *             set in cms/components/_learn_search.html)
 *   offset:   navbar height in px, subtracted from the observer root so the swap
 *             lands as the block slides under the bar rather than off-screen
 *
 * Pages with no search block (category, post) have no sentinel — the search bar
 * shows immediately, which is what the reference does there.
 */
export default class extends ApplicationController {
  static targets = ['tabs', 'search'];

  static values = {
    sentinel: { type: String, default: '[data-nav-search-sentinel]' },
    offset: { type: Number, default: 70 },
  };

  declare readonly tabsTarget: HTMLElement;
  declare readonly searchTarget: HTMLElement;
  declare readonly hasTabsTarget: boolean;
  declare readonly hasSearchTarget: boolean;
  declare sentinelValue: string;
  declare offsetValue: number;

  private observer?: IntersectionObserver;

  connect() {
    const sentinel = document.querySelector(this.sentinelValue);

    if (!sentinel) {
      this.showSearch();
      return;
    }

    this.showTabs();

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.showTabs();
        } else {
          this.showSearch();
        }
      },
      { rootMargin: `-${this.offsetValue}px 0px 0px 0px` },
    );
    this.observer.observe(sentinel);
  }

  disconnect() {
    this.observer?.disconnect();
    this.observer = undefined;
  }

  private showTabs() {
    this.swap(true);
  }

  private showSearch() {
    this.swap(false);
  }

  private swap(tabsVisible: boolean) {
    if (this.hasTabsTarget) {
      this.setVisible(this.tabsTarget, tabsVisible);
    }
    if (this.hasSearchTarget) {
      this.setVisible(this.searchTarget, !tabsVisible);
    }
  }

  /**
   * `invisible` (visibility: hidden) rather than `hidden` (display: none) so the
   * opacity transition still runs and the inactive control leaves the tab order.
   */
  private setVisible(element: HTMLElement, visible: boolean) {
    element.classList.toggle('opacity-0', !visible);
    element.classList.toggle('invisible', !visible);
  }
}
